'use client';

import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { authStore } from '@/infrastructure/store/authStore';

/** The API sends this when the account holds no role in the current partner. */
const UNASSIGNED_ROLE_NAME = 'not-assigned';

interface IUseCurrentUserRoleResult {
  /** Ready to render: the catalog description, or an empty string when there is nothing to say. */
  roleLabel: string;
}

/**
 * Turns the role the session carries into text a person can read. `userRoleName` is an identifier
 * (`umodeSystemAdmin`), so the label comes from the partner's catalog and only falls back to the
 * raw name while the catalog has not arrived, or when it does not describe this role.
 */
export function useCurrentUserRole(): IUseCurrentUserRoleResult {
  const { userRoleName, userClearanceRoles } = useStore(
    authStore,
    useShallow((state) => ({
      userRoleName: state.user?.userRoleName ?? '',
      userClearanceRoles: state.userClearanceRoles,
    })),
  );

  if (!userRoleName || userRoleName === UNASSIGNED_ROLE_NAME) {
    return { roleLabel: '' };
  }

  const currentRole = (userClearanceRoles ?? []).find((role) => role.roleName === userRoleName);

  return { roleLabel: currentRole?.roleDescriptionPT?.trim() || userRoleName };
}
